import type { CharacterState } from '@/physics/character';
import { blendPose, copyPose, ease, neutralPose, type Clip, type Pose } from './types';

export interface ClipSet {
  idle: Clip; walk: Clip; trot: Clip; creep: Clip; roll: Clip; landing: Clip;
  /** Hauling onto a ledge. Driven by the controller's progress, not by a clock. */
  scramble: Clip;
}

export interface PlayerTuning {
  /** Crossfade seconds between locomotion clips. */
  blendTime: number;
  /** Crossfade seconds into and out of a one-shot (landing). */
  oneShotBlendTime: number;
  /** Metres travelled per full walk cycle. Drives phase from distance, not time. */
  walkStride: number;
  /** Metres per full trot cycle. */
  trotStride: number;
  /** Metres per full creep cycle. */
  creepStride: number;
  /** Metres per full roll revolution. */
  rollStride: number;
  /** Landing impact (m/s) below which no landing one-shot fires at all. */
  landingThreshold: number;
  /** Impact speed that maps to a full-strength landing. */
  landingFullImpact: number;
}

/** Intentionally un-tuned first guesses for the first in-engine feel pass. */
export const ROSALIE_ANIM: PlayerTuning = {
  // WHY: a quarter second keeps gait changes legible without looking sluggish.
  blendTime: 0.25,
  // WHY: a short envelope preserves the impact while avoiding a hard layer pop.
  oneShotBlendTime: 0.12,
  // WHY: 0.62 m gives the compact walk a deliberate, non-sliding cadence.
  walkStride: 0.62,
  // WHY: 0.82 m lengthens the eager trot without multiplying leg frequency too far.
  trotStride: 0.82,
  // WHY: 0.44 m makes the restrained creep place feet more frequently.
  creepStride: 0.44,
  // WHY: 0.72 m approximates one small-body roll revolution.
  rollStride: 0.72,
  // WHY: gentle step-downs should not spend the landing animation.
  landingThreshold: 2.2,
  // WHY: 6 m/s is a strong fall for her scale and deserves the full response.
  landingFullImpact: 6,
};

/**
 * How far she will lean to follow the ground, radians.
 *
 * The test room's ramp is the shallow case. The cap exists for the steep one:
 * matching a wall would stand her on her nose, and the controller refuses slopes
 * past its own climb angle anyway, so anything beyond this is a surface she is
 * not really standing on.
 */
const MAX_SLOPE_PITCH = 0.5;

/** How fast the lean chases the ground, per second. Fast enough to look attached. */
const SLOPE_PITCH_RESPONSE = 9;

export class AnimationPlayer {
  readonly debug: {
    from: string; to: string; weight: number; phase: number;
    /**
     * Total locomotion cycles since boot, NOT wrapped.
     *
     * `phase` is the wrapped version and is what the rig needs. Anything trying
     * to count events per cycle needs this one instead: from a wrapped value you
     * cannot distinguish "advanced a little" from "advanced a whole cycle and a
     * little", and the audio's footfall trigger was silently dropping steps on a
     * long frame because of exactly that. Counting is the caller's job; not
     * losing the information is ours.
     */
    cycles: number;
  } = {
    from: 'idle', to: 'idle', weight: 1, phase: 0, cycles: 0,
  };

  readonly #clips: ClipSet;
  readonly #tuning: PlayerTuning;
  readonly #blendFrom = neutralPose();
  readonly #clipPose = neutralPose();
  readonly #basePose = neutralPose();
  readonly #landingPose = neutralPose();
  #currentClip: Clip;
  #currentId = 'idle';
  #fromId = 'idle';
  #locomotionPhase = 0;
  #idlePhase = 0;
  #scramblePhase = 0;
  #blendElapsed = 0;
  #blending = false;
  #landingElapsed = 0;
  #landingStrength = 0;
  #landingPlaying = false;
  #slopePitch = 0;
  #locomotionCycles = 0;

  constructor(clips: ClipSet, tuning: PlayerTuning) {
    this.#clips = clips;
    this.#tuning = tuning;
    this.#currentClip = clips.idle;
    this.reset();
  }

  /** Advance and produce the blended pose. `dt` is REAL frame time. */
  update(dt: number, state: CharacterState, out: Pose): void {
    const frameDt = Math.max(0, dt);
    const targetClip = this.#clipForGait(state.gait);

    this.#scramblePhase = state.scramble;
    this.#advancePhase(frameDt, state);
    if (this.#blending) this.#blendElapsed += frameDt;
    if (targetClip.id !== this.#currentId) {
      this.#sampleCurrentPose(this.#blendFrom);
      this.#fromId = this.#currentId;
      this.#currentClip = targetClip;
      this.#currentId = targetClip.id;
      this.#blendElapsed = 0;
      this.#blending = true;
    }

    this.#sampleCurrentPose(this.#basePose);
    this.#startLandingIfNeeded(state.landingImpact);
    this.#applyLanding(frameDt, out);
    this.#applySlope(frameDt, state, out);
    this.#updateDebug();
  }

  /**
   * Pitch her body to lie along the ground she is standing on.
   *
   * Reported at review: on the ramp she stayed level "like on a plane and thus
   * the rear legs are in the air a bit". Correct — every clip authors pitch
   * relative to flat ground, so on a 20° ramp she was 20° wrong, and because her
   * legs are rigid pivots (D-015) the error shows up as feet leaving the floor
   * rather than as a subtly odd posture.
   *
   * Layered here rather than in the clips because it belongs to the WORLD, not
   * to the gait. Putting it in the clips would mean every future clip having to
   * remember to handle slopes, and one of them eventually would not.
   *
   * `slopeGrade` is a tangent — rise over run along her facing — so the angle is
   * its arctangent. Smoothed on the way in: ground normals change discontinuously
   * at the lip of a ramp, and snapping her pitch there reads as a flinch.
   */
  #applySlope(dt: number, state: CharacterState, out: Pose): void {
    const target = state.grounded ? Math.atan(state.slopeGrade) : 0;
    const clamped = Math.max(-MAX_SLOPE_PITCH, Math.min(MAX_SLOPE_PITCH, target));
    const k = Math.min(1, dt * SLOPE_PITCH_RESPONSE);
    this.#slopePitch += (clamped - this.#slopePitch) * k;
    // Nose DOWN when facing downhill, nose UP when climbing.
    //
    // The sign here was INVERTED on the first attempt, under a comment stating
    // the opposite convention with complete confidence, and a screenshot I read
    // as confirming it. It was caught by independent review and then settled by
    // measurement: `she leans into the slope, and the right way round` compares
    // her snout against her tail in world space on a real ramp.
    //
    // Positive `pose.pitch` puts her nose DOWN — check `scrambleClip`, which
    // uses a negative value where its own comment says "nose up". `slopeGrade`
    // is positive downhill, so it adds.
    out.pitch += this.#slopePitch;
  }

  /** Reset to idle at phase 0. */
  reset(): void {
    this.#currentClip = this.#clips.idle;
    this.#currentId = this.#clips.idle.id;
    this.#fromId = this.#currentId;
    this.#locomotionPhase = 0;
    this.#idlePhase = 0;
    this.#blendElapsed = 0;
    this.#blending = false;
    this.#landingElapsed = 0;
    this.#landingStrength = 0;
    this.#landingPlaying = false;
    this.#slopePitch = 0;
    this.#locomotionCycles = 0;
    this.#updateDebug();
  }

  #advancePhase(dt: number, state: CharacterState): void {
    if (state.gait === 'scramble') return;
    if (state.gait === 'still') {
      this.#idlePhase = wrapPhase(this.#idlePhase + dt / safeDuration(this.#clips.idle.duration));
      return;
    }
    // Distance, rather than an animation clock, is the locomotion phase source.
    const advance = (state.speed * dt) / this.#strideFor(state.gait);
    this.#locomotionCycles += advance;
    this.#locomotionPhase = wrapPhase(this.#locomotionPhase + advance);
  }

  #sampleCurrentPose(out: Pose): void {
    this.#currentClip.sample(this.#phaseFor(this.#currentClip), this.#clipPose);
    if (!this.#blending) {
      copyPose(this.#clipPose, out);
      return;
    }

    const weight = Math.min(1, this.#blendElapsed / safeDuration(this.#tuning.blendTime));
    blendPose(this.#blendFrom, this.#clipPose, ease.inOut(weight), out);
    if (weight === 1) this.#blending = false;
  }

  #applyLanding(dt: number, out: Pose): void {
    if (!this.#landingPlaying) {
      copyPose(this.#basePose, out);
      return;
    }

    this.#landingElapsed += dt;
    const duration = safeDuration(this.#clips.landing.duration);
    const phase = Math.min(1, this.#landingElapsed / duration);
    const blendTime = safeDuration(this.#tuning.oneShotBlendTime);
    const blendIn = ease.out(Math.min(1, this.#landingElapsed / blendTime));
    const blendOut = ease.out(Math.max(0, Math.min(1, (duration - this.#landingElapsed) / blendTime)));
    const weight = this.#landingStrength * Math.min(blendIn, blendOut);
    this.#clips.landing.sample(phase, this.#landingPose);
    blendPose(this.#basePose, this.#landingPose, weight, out);
    if (phase === 1) this.#landingPlaying = false;
  }

  #startLandingIfNeeded(impact: number): void {
    if (this.#landingPlaying || impact <= this.#tuning.landingThreshold) return;
    const impactRange = this.#tuning.landingFullImpact - this.#tuning.landingThreshold;
    this.#landingStrength = impactRange > 0
      ? Math.min(1, (impact - this.#tuning.landingThreshold) / impactRange)
      : 1;
    this.#landingElapsed = 0;
    this.#landingPlaying = true;
  }

  #clipForGait(gait: CharacterState['gait']): Clip {
    switch (gait) {
      case 'still': return this.#clips.idle;
      case 'creep': return this.#clips.creep;
      case 'walk': return this.#clips.walk;
      case 'trot': return this.#clips.trot;
      case 'roll': return this.#clips.roll;
      case 'scramble': return this.#clips.scramble;
    }
  }

  #phaseFor(clip: Clip): number {
    // The scramble is driven by the controller, so its phase is authoritative:
    // the clip has to stay locked to where she actually is on the ledge, or her
    // legs finish reaching while her body is still climbing.
    if (clip.id === this.#clips.scramble.id) return this.#scramblePhase;
    return clip.id === this.#clips.idle.id ? this.#idlePhase : this.#locomotionPhase;
  }

  #strideFor(gait: Exclude<CharacterState['gait'], 'still'>): number {
    switch (gait) {
      case 'creep': return safeDuration(this.#tuning.creepStride);
      case 'walk': return safeDuration(this.#tuning.walkStride);
      case 'trot': return safeDuration(this.#tuning.trotStride);
      case 'roll': return safeDuration(this.#tuning.rollStride);
      // A scramble is not locomotion: its phase comes from the controller's
      // scramble progress, not from distance travelled. Never divided by.
      case 'scramble': return safeDuration(this.#tuning.walkStride);
    }
  }

  #updateDebug(): void {
    this.debug.from = this.#blending ? this.#fromId : this.#currentId;
    this.debug.to = this.#currentId;
    this.debug.weight = this.#blending
      ? ease.inOut(Math.min(1, this.#blendElapsed / safeDuration(this.#tuning.blendTime)))
      : 1;
    this.debug.phase = this.#currentId === this.#clips.idle.id ? this.#idlePhase : this.#locomotionPhase;
    this.debug.cycles = this.#locomotionCycles;
  }
}

function safeDuration(value: number): number {
  return Math.max(Number.EPSILON, value);
}

function wrapPhase(phase: number): number {
  return phase - Math.floor(phase);
}
